import { Card, Text, TextList, TextListItem } from '@oneaudi/unified-web-alpha-components';
import { useAtomValue } from 'jotai';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { CompareLayerWrapper } from '~/app/pages/configurator/components/compare-layer-wrapper';
import { EuTyreLabel } from '~/app/pages/configurator/components/more-information-dialog/components/eu-tyre-label/eu-tyre-label';
import { MoreInformationDialogHeader } from '~/app/pages/configurator/components/more-information-dialog/components/more-information-dialog-header';
import { useDebugMode } from '~/app/utils/debug-mode';
import { useFootnotes } from '~/components/footnotes';
import { RenderHtml } from '~/components/text/render-html';
import { ConfiguredCarFeatureFragment, ConfiguredCarPackageFeatureFragment } from '~/graphql/__generated__/graphql';
import { useFinancing } from '~/graphql/hooks/use-financing';
import { PriceOrError } from '~/graphql/mapper';
import { CatalogItemType, ContentOrPackage } from '~/graphql/types';
import GetUrlFromImageSet, { GetUrlArrayFromImageSet } from '~/graphql/utils/get-from-image-set';
import { sanitizeText } from '~/utils/sanitize-text';
import { PackageContent } from '../package-content/package-content';
import { StyledContentContainer, StyledDialogSection } from './styles/more-information-dialog-content-styles';

import { MONTHLY_RATE_DIALOG_REMARK } from '~/components/footnotes/constants';
import { useCatalog } from '~/graphql/hooks/use-configurator';
import { state } from '~/storage';

type MoreInformationProperties = {
	headline: string;
	imageUrl: string | null | undefined;
	imageUrls: string[];
	introText: string | null | undefined;
	description: string | null | undefined;
	benefits: string[] | null | undefined;
	featureText: string | null | undefined;
	remarks: string | null | undefined;
};

export function getItemInformation(featureOrPackage: ContentOrPackage): MoreInformationProperties {
	const { texts } = featureOrPackage;
	const defaultInfo: MoreInformationProperties = {
		headline: sanitizeText(featureOrPackage.name ?? ''),
		imageUrl:
			featureOrPackage.__typename === 'ConfiguredCarPackageFeature'
				? featureOrPackage?.images?.smallImageHD?.url || featureOrPackage?.images?.outerTileHD?.url
				: GetUrlFromImageSet(featureOrPackage?.imageSets, 'small_hd_image') ||
					GetUrlFromImageSet(featureOrPackage?.imageSets, 'outer_tile_hd') ||
					GetUrlFromImageSet(featureOrPackage?.imageSets, 'hd_thumbnail'),
		imageUrls:
			featureOrPackage.__typename !== 'ConfiguredCarPackageFeature'
				? GetUrlArrayFromImageSet(featureOrPackage?.imageSets, 'small_hd_image')?.filter((url): url is string =>
						Boolean(url),
					) || []
				: [],
		introText: texts?.intro,
		description: texts?.akText,
		benefits: texts?.benefits as string[],
		featureText: texts?.akText,
		remarks: texts?.akRemarks,
	};

	if (featureOrPackage.__typename === 'ConfiguredCarPackage') {
		if (featureOrPackage.featureGroup === 'RIMS') {
			defaultInfo.imageUrl = GetUrlFromImageSet(featureOrPackage.imageSets, 'wheel_image');
		}
		if (featureOrPackage.featureGroup === 'INTERIOR_TILES') {
			defaultInfo.imageUrl = GetUrlFromImageSet(featureOrPackage.imageSets, 'interior_tile_hd_square');
			defaultInfo.featureText = featureOrPackage?.content?.at(0)?.texts?.akText;
		}
	}

	return defaultInfo;
}

export function MoreInformationDialogContent({
	featureOrPackage,
	onAddToConfiguration,
	hidePriceAndAvailability,
	setSubItem,
	priceOverride,
	rateOverride,
}: {
	featureOrPackage: ContentOrPackage;
	onAddToConfiguration: (() => void) | undefined;
	hidePriceAndAvailability?: boolean;
	setSubItem?: (item: ConfiguredCarFeatureFragment | ConfiguredCarPackageFeatureFragment) => void;
	priceOverride?: PriceOrError | undefined | null;
	rateOverride?: number | undefined | null;
}) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const vatConfig = useVATConfig();
	const { getName } = useDebugMode();
	const { imageUrl, imageUrls, headline, featureText, introText, benefits, remarks } =
		getItemInformation(featureOrPackage);

	const mockFeature = {
		price: null,
		rate: null,
		featureGroup: null,
		pr7: '',
		tyreLabels: null,
		contentId: '',
		...featureOrPackage,
	};

	// https://collaboration.msi.audi.com/jira/browse/NWM-159
	// Onegraph sometimes returns benefits and tyre labels with null values
	const sanitizedBenefits = (benefits ?? []).filter(Boolean);
	const sanitizedTyreLabels = (mockFeature.tyreLabels ?? []).filter((label) => !!label?.tyreLabelUrl);
	// FIXME: should be replaced when the new financing-footnote-replacement is made
	const { partRateByPr } = useFinancing();
	const monthlyRate = partRateByPr(mockFeature.pr7);
	const monthlyRateAvailable = rateOverride ?? monthlyRate;

	const { addFootnote, removeFootnote } = useFootnotes();
	const disclaimer = useAtomValue(state.financingDisclaimerText);

	if (!monthlyRateAvailable) {
		removeFootnote(MONTHLY_RATE_DIALOG_REMARK);
	} else if (
		config.enableFinancingServices &&
		!config.hideMonthlyRates &&
		config.showFinancingDisclaimerInDialogs &&
		!mockFeature.status.standard
	) {
		addFootnote(MONTHLY_RATE_DIALOG_REMARK, disclaimer);
	}

	return (
		<StyledContentContainer>
			<MoreInformationDialogHeader
				headline={getName({ name: headline, pr7: mockFeature.pr7 ?? mockFeature.contentId }) ?? ''}
				price={priceOverride ?? mockFeature.price}
				pr7={mockFeature.pr7}
				monthlyRateOverride={rateOverride}
				vatConfig={vatConfig.feature}
				status={mockFeature.status}
				onAddToConfiguration={onAddToConfiguration}
				imageUrl={imageUrl}
				imageUrls={imageUrls}
				imageFit={mockFeature.featureGroup === 'RIMS' ? 'object-contain' : 'object-cover'}
				hidePriceAndAvailability={hidePriceAndAvailability}
				disclaimerStartDate={mockFeature.texts?.disclaimerStartDate || ''}
				disclaimerEndDate={mockFeature.texts?.disclaimerEndDate || ''}
				disclaimerHeadline={mockFeature.texts?.disclaimerHeadline || ''}
				disclaimerContent={mockFeature.texts?.disclaimerText || ''}
			/>
			{introText && (
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					<span tabIndex={0} dangerouslySetInnerHTML={{ __html: introText }} />
				</Text>
			)}
			{sanitizedBenefits.length > 0 && (
				<StyledDialogSection tabIndex={0}>
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
						<span dangerouslySetInnerHTML={{ __html: i18n('ngc.dialogs.benefits') }} />
					</Text>
					<TextList size="md" variant={'bullet'}>
						{sanitizedBenefits.map((benefit, index) => (
							<TextListItem key={index}>
								<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
									<span dangerouslySetInnerHTML={{ __html: benefit }} />
								</Text>
							</TextListItem>
						))}
					</TextList>
				</StyledDialogSection>
			)}
			{featureText && (
				<StyledDialogSection tabIndex={0}>
					<Text className="features" variant={{ type: 'copy', order: '1', style: 'bold' }}>
						<span dangerouslySetInnerHTML={{ __html: i18n('ngc.dialogs.features') }} />
					</Text>

					<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
						<RenderHtml html={featureText} />
					</Text>
				</StyledDialogSection>
			)}
			{featureOrPackage.__typename === 'ConfiguredCarPackage' &&
				featureOrPackage.content &&
				featureOrPackage.content.length > 0 && (
					<PackageContent setSubItem={setSubItem} packageData={featureOrPackage} />
				)}
			{remarks?.trim() && (
				<Card>
					<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
						<RenderHtml html={remarks} />
					</Text>
				</Card>
			)}
			{sanitizedTyreLabels.length > 0 && <EuTyreLabel tyreLabels={sanitizedTyreLabels} />}
		</StyledContentContainer>
	);
}

export function MoreInformationTrimlineDialogContent({
	trimline,
	onAddToConfiguration,
	setOpen,
	hasSingleTrimlineAndEngine,
	open,
}: {
	trimline: CatalogItemType['trimline'];
	onAddToConfiguration: (() => void) | undefined;
	setOpen: (open: boolean) => void;
	hasSingleTrimlineAndEngine?: boolean;
	open: boolean;
}) {
	const { trimlines } = useCatalog();

	const driveTrains = trimlines?.flatMap((t) => t.driveTrains) ?? [];
	const selectedDriveTrain = driveTrains?.find((d) => d.status.selected);
	const cheapestDrivetrain = trimline?.driveTrains?.sort((a, b) => {
		const priceA = a?.totalPrice?.value || 0;
		const priceB = b?.totalPrice?.value || 0;
		return priceA - priceB;
	})?.[0];
	const driveTrain =
		trimline?.driveTrains?.find((d) => d.modelId.code === selectedDriveTrain?.modelId.code) || cheapestDrivetrain;

	return (
		<CompareLayerWrapper
			key="more-information-trimline-dialog-content"
			setIsOpen={setOpen}
			selectedDriveTrain={driveTrain}
			open={open}
			actions={{
				onAddToConfiguration,
			}}
			config={{
				selectedTab: 'standardEquipment',
				isDisabled: hasSingleTrimlineAndEngine,
			}}
		/>
	);
}
